// Waypoints layer family — NASR navaids (VOR / VOR-DME / VORTAC / TACAN / NDB / DME).
//
// One GeoJSON source (`waypoints`) drives two layers:
//   - waypoints-icon: FAA Sectional-style icons (hexagon variants for the
//     VOR family, dotted ring for NDB) so the operational class is
//     recognizable at a glance. Visible from z>=7.
//   - waypoints-label: ident text label above the icon, visible from z>=9.
//
// NOTE: id suffix '-mount' to avoid colliding with MapView's existing
// registerStyleRebuild entry (id 'waypoints') that owns the data re-push.
// The '-mount' suffix follows the terrain.ts/dipSites.ts precedent for
// this exact situation: the controller owns apply behavior; this module
// only owns the mount.
// See src/map/layers/terrain.ts for the canonical '-mount' pattern.
// Extracted from addOverlays.ts as HI-1 batch 8 — final batch (2026-05-24).
// Pure mechanical move — source spec, paint expressions, layer ids, minzoom,
// and layer order all byte-identical to the pre-extraction form.
// ensureNavaidIcons + NAVAID_ICON_EXPRESSION moved here from addOverlays.ts
// (were only used in this block).

import type { Map as MlMap } from 'maplibre-gl';
import { EMPTY_FC } from '../MapView';
import { ensureNavaidIcons, NAVAID_ICON_EXPRESSION } from '../navaidIcons';
import { registerLayerFamily } from '../layerVisibilityRegistry';
import { useLayerVisibilityStore } from '../../stores/layerVisibilityStore';

/**
 * Mount the waypoints source and layers.
 * Idempotent on the source.
 */
export function addWaypointsLayers(map: MlMap): void {
  if (!map.getSource('waypoints')) {
    map.addSource('waypoints', { type: 'geojson', data: EMPTY_FC });
    void ensureNavaidIcons(map);
    map.addLayer({
      id: 'waypoints-icon',
      type: 'symbol',
      source: 'waypoints',
      minzoom: 7,
      layout: {
        'icon-image': NAVAID_ICON_EXPRESSION,
        'icon-size': [
          'interpolate', ['linear'], ['zoom'],
          7, 0.65,
          10, 0.95,
          13, 1.2,
        ],
        'icon-allow-overlap': true,
        'icon-ignore-placement': true,
      },
    });
    map.addLayer({
      id: 'waypoints-label',
      type: 'symbol',
      source: 'waypoints',
      minzoom: 9,
      layout: {
        'text-field': ['get', 'ident'],
        'text-size': 10,
        'text-offset': [0, 0.9],
        'text-anchor': 'top',
        'text-font': ['Noto Sans Regular'],
      },
      paint: {
        'text-color': '#cbd5e1',
        'text-halo-color': '#0b0d0f',
        'text-halo-width': 1.2,
      },
    });
  }
}

// NOTE: id suffix '-mount' to avoid colliding with MapView's existing
// registerStyleRebuild entry (id 'waypoints') that owns the data re-push
// after a basemap rebuild.
//
// 2026-05-28 fix: the navaid chip on AeroKnob wasn't toggling the layer
// off because the family had no `visibilitySelector` — the registry's
// applyLayerVisibility() short-circuits when that's missing (registry.ts
// line 303), so toggleLayer('waypoints') flipped the store value but no
// apply path ever ran. The MapView 'waypoints' registerStyleRebuild
// entry only handles data re-push after basemap rebuilds; it doesn't
// react to visibility changes. Add the missing selector + applyState so
// the chip actually flips MapLibre visibility on both child layers.
registerLayerFamily({
  id: 'waypoints-mount',
  label: 'Waypoints (navaids) mount',
  sourceId: 'waypoints',
  layerIds: ['waypoints-icon', 'waypoints-label'],
  mount: (map) => addWaypointsLayers(map),
  visibilitySelector: (l) => l.waypoints,
  applyState: (map) => {
    if (!map.getLayer('waypoints-icon')) return;
    const show = useLayerVisibilityStore.getState().layers.waypoints;
    const v = show ? 'visible' : 'none';
    map.setLayoutProperty('waypoints-icon', 'visibility', v);
    if (map.getLayer('waypoints-label')) {
      map.setLayoutProperty('waypoints-label', 'visibility', v);
    }
  },
});
